import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Clock, Globe, Users } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 30, filter: 'blur(10px)' },
  visible: { opacity: 1, y: 0, filter: 'blur(0px)' }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

const values = [
{
  icon: Award,
  title: 'Excellence',
  description: 'Un engagement constant vers les plus hauts standards de qualité et de professionnalisme.'
},
{
  icon: Clock,
  title: 'Pérennité',
  description: 'Une vision à long terme pour préserver et faire fructifier votre patrimoine à travers les générations.'
},
{
  icon: Globe,
  title: 'Expertise internationale',
  description: 'Une connaissance approfondie des environnements juridiques et fiscaux franco-belges.'
},
{
  icon: Users,
  title: 'Proximité',
  description: 'Une relation de confiance basée sur l\'écoute, la disponibilité et la discrétion.'
}];


export default function AboutPage() {
  return (
    <div className="page-wrapper" data-testid="about-page">
      {/* Hero Section */}
      <section className="hero-section relative min-h-[60vh] flex items-center overflow-hidden" data-testid="about-hero">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_cf0aab81-969a-4e9c-9793-f40790513670/artifacts/ozk0drws_IMAGE_PAGE_GARDE_CONSOLIDATION.png')`,
            opacity: 0.7
          }} />

        <div className="hero-overlay" />
        <div className="hero-content w-full pt-32 pb-20">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
              className="max-w-3xl">

              <motion.p
                variants={fadeUp}
                transition={{ duration: 0.8 }}
                className="text-[#C5A059] text-xs tracking-[0.3em] uppercase mb-4">

                À propos
              </motion.p>
              <motion.h1
                variants={fadeUp}
                transition={{ duration: 0.8, delay: 0.1 }}
                className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#FDFBF7] font-medium tracking-tight leading-tight mb-6">

                Une histoire de<br />
                <span className="text-gold-gradient">confiance</span>
              </motion.h1>
              <motion.div
                variants={fadeUp}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="gold-line mb-8" />

              <motion.p
                variants={fadeUp}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="text-[#FDFBF7]/70 text-lg md:text-xl leading-relaxed">

                Depuis plus de dix ans, notre équipe accompagne les familles 
                et entrepreneurs dans la gestion globale de leur patrimoine.
              </motion.p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 md:py-32 bg-[#FDFBF7]" data-testid="story-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}>

              <p className="text-[#C5A059] text-xs tracking-[0.3em] uppercase mb-4">
                Notre histoire
              </p>
              <h2 className="md:text-4xl !font-serif !font-medium !text-3xl mb-6 text-[#0B1221]">Votre Family Office

              </h2>
              <div className="gold-line mb-8" />
              <div className="space-y-6 text-[#64748B] leading-relaxed">
                <p className="!text-justify">Fondé sur les principes d'excellence et de discrétion, notre Family office s'est imposé comme un acteur de référence dans l'accompagnement patrimonial d'un nombre intentionnellement limité de familles prospères.



                </p>
                <p className="!text-justify">Notre structure nous permet d'agir exclusivement dans l'intérêt de nos clients, sans conflits d'intérêts liés à la distribution de produits financiers. Nous travaillons exclusivement sur base d'honoraires communiqués préalablement via une lettre de mission.



                </p>
                <p className="!text-justify">
                  Basés à Tournai, nous avons développé une expertise 
                  unique dans les problématiques transfrontalières franco-belges, 
                  permettant à nos clients de bénéficier d'une approche globale 
                  et optimisée de leur patrimoine.
                </p>
              </div>
            </motion.div>

            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}>

              <div className="image-frame">
                <img
                  src="https://images.unsplash.com/photo-1761437856297-e7d0191f9a42?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Art abstrait doré"
                  className="w-full h-[400px] md:h-[500px] object-cover" />

              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 md:py-32 bg-[#F5F2EB]" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16 md:mb-20">

            <p className="text-[#C5A059] text-xs tracking-[0.3em] uppercase mb-4">
              Nos engagements
            </p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#0B1221] font-medium tracking-tight">
              Nos valeurs
            </h2>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 gap-8">

            {values.map((value, index) =>
            <motion.div
              key={index}
              variants={fadeUp}
              transition={{ duration: 0.6 }}
              className="bg-white p-10 md:p-12 border border-[#C5A059]/10 hover:border-[#C5A059]/30 transition-colors duration-500"
              data-testid={`value-card-${index}`}>

                <value.icon
                size={36}
                className="text-[#C5A059] mb-6"
                strokeWidth={1.5} />

                <h3 className="font-serif text-2xl text-[#0B1221] mb-4">
                  {value.title}
                </h3>
                <p className="!text-justify text-[#64748B]">
                  {value.description}
                </p>
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Franco-Belgian Expertise */}
      <section className="py-24 md:py-32 bg-[#0B1221]" data-testid="expertise-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative">

              <img
                src="https://images.unsplash.com/photo-1761437856311-3ba13025f161?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Art abstrait doré"
                className="w-full h-[350px] md:h-[450px] object-cover" />

              <div className="absolute inset-0 border border-[#C5A059]/30" />
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}>

              <p className="text-[#C5A059] text-xs tracking-[0.3em] uppercase mb-4">
                Notre positionnement
              </p>
              <h2 className="font-serif text-3xl md:text-4xl text-[#FDFBF7] font-medium tracking-tight mb-6">
                L'axe Paris-Bruxelles
              </h2>
              <div className="gold-line mb-8" />
              <div className="space-y-6 text-[#FDFBF7]/70 leading-relaxed">
                <p className="!text-justify">
                  Notre implantation stratégique entre la France et la Belgique 
                  nous confère une expertise unique pour accompagner les familles 
                  ayant des intérêts dans ces deux juridictions.
                </p>
                <p className="!text-justify">
                  Qu'il s'agisse de questions de résidence fiscale, de planification 
                  successorale transfrontalière ou de structuration d'actifs, 
                  nous apportons une vision complète et coordonnée.
                </p>
              </div>
              <div className="mt-10 flex flex-col sm:flex-row gap-8">
                <div>
                  <p className="text-[#C5A059] font-serif text-4xl mb-2">10+</p>
                  <p className="text-[#FDFBF7]/50 text-sm tracking-wide uppercase">Années d'expertise</p>
                </div>
                <div>
                  <p className="text-[#C5A059] font-serif text-4xl mb-2">1</p>
                  <p className="text-[#FDFBF7]/50 text-sm tracking-wide uppercase">Bureau à Tournai</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#FDFBF7]" data-testid="about-cta">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}>

            <p className="text-[#C5A059] text-xs tracking-[0.3em] uppercase mb-4">
              Rencontrons-nous
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#0B1221] font-medium tracking-tight mb-6">
              Échangeons sur vos projets
            </h2>
            <p className="text-[#64748B] text-lg max-w-xl mx-auto mb-10 leading-relaxed">
              Nous serions ravis de vous rencontrer pour comprendre vos objectifs 
              et vous présenter notre approche personnalisée.
            </p>
            <Link
              to="/contact"
              className="btn-gold inline-flex items-center gap-3"
              data-testid="about-cta-button">

              Prendre contact
              <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>);

}